import type { ButtonHTMLAttributes } from 'react';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon/Icon';
import { type GlyphName, ObjectGlyph } from '../ObjectGlyph/ObjectGlyph';
import { Tooltip } from '../Tooltip/Tooltip';

/**
 * An icon-only button. `label` is mandatory: an icon without an accessible name
 * is a guessing game, and on touch there is no tooltip to fall back on.
 *
 * ITS LABEL IS A HELP TAG, NOT A `title` ATTRIBUTE (SH-A9, owner-37: the "Hide the
 * details" tag stood at the top left of the window while its button was in the toolbar's
 * trailing group). A tooltip "displays a small, transient view that briefly describes how
 * to use a component in the interface" and the help manager places it "at or near the
 * point on the screen specified"
 * (https://developer.apple.com/design/human-interface-guidelines/offering-help,
 * https://developer.apple.com/documentation/appkit/nshelpmanager/showcontexthelp(for:locationhint:)).
 * A native `title` is the browser's own tag and the browser draws it where it likes -
 * measured on this build: at the window corner, seconds late, with no `role="tooltip"` for
 * anything to find. The primitive puts it at the control, and because EVERY icon-only
 * button in the product had the same `title`, fixing it here fixes all of them.
 *
 * `help={false}` is for the two places where a tag would be noise: a control whose label
 * is already visible beside it, and a trigger that opens a panel naming itself.
 *
 * WHAT IT DRAWS IS EITHER A HAIRLINE ICON OR ONE OF OUR OWN SYMBOLS, never both
 * (2026-09-10, when the owner gave the sidebar toggle the `sidebar` symbol). The two
 * lists overlap by name - `settings` is an `IconName` AND a `GlyphName` - so one prop
 * that took either would be ambiguous at the call site and would silently draw the
 * wrong drawing. Outside a tile `ObjectGlyph` renders FLAT and its body stops fall
 * back to `currentColor`, so a symbol here is a monochrome mark in the button's ink,
 * like an SF Symbol in a toolbar, and the group around it keeps owning the capsule.
 */
export type IconButtonVariant = 'plain' | 'glass';
export type IconButtonSize = 'sm' | 'md';

export type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  readonly label: string;
  readonly variant?: IconButtonVariant | undefined;
  readonly size?: IconButtonSize | undefined;
  /** The help tag. On by default; `false` where the label is already on the screen. */
  readonly help?: boolean | undefined;
} & (
    | { readonly icon: IconName; readonly glyph?: undefined }
    | { readonly icon?: undefined; readonly glyph: GlyphName }
  );

const BASE =
  // Same token as Button: our actions are capsules, and a square icon button next
  // to a pill is the detail that gives the whole thing away.
  'inline-flex items-center justify-center rounded-(--radius-button) transition-colors ' +
  'disabled:opacity-50';

const SIZES: Record<IconButtonSize, string> = {
  // The square IS the touch floor, read from `--touch-target-min` so it is 44 on a
  // phone and 40 on desktop (E5); `sm` exists for dense desktop rows and reaches the
  // floor with `.hit-expand` instead of by growing (`ui-foundation.md` R5, wave 2).
  md: 'size-(--touch-target-min) text-md',
  sm: 'hit-expand size-8 text-base',
};

const VARIANTS: Record<IconButtonVariant, string> = {
  // `plain` is for icons INSIDE data surfaces (row actions); anything sitting
  // on chrome or the canvas is a glass capsule like every other control.
  // Same split as Button: glass presses by giving, plain presses by filling.
  plain: 'pressable text-ink-muted hover:bg-surface-hover hover:text-ink',
  glass: 'glass-control text-ink',
};

export function IconButton({
  icon,
  glyph,
  label,
  variant = 'plain',
  size = 'md',
  help = true,
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  const button = (
    <button
      type={type}
      aria-label={label}
      /** The kit's SYMBOL button master: a square, never sized to a label (see `Button`). */
      data-control="symbol"
      className={cx(BASE, SIZES[size], VARIANTS[variant], className)}
      {...rest}
    >
      {icon !== undefined ? <Icon name={icon} /> : <ObjectGlyph name={glyph} />}
    </button>
  );
  /**
   * THE PROPS STILL LAND ON THE BUTTON, which is what lets this button keep being the
   * trigger of a menu. A wrapper that took `...rest` itself would swallow the handlers and
   * the ref a `DropdownMenu` passes down through its own `asChild`; the tooltip only wraps
   * what is already finished.
   */
  return help ? <Tooltip label={label}>{button}</Tooltip> : button;
}
